import { COUNTRIES as countryNames } from '~/consts/priceParrot';

import type { LensFilterValues } from '~/types/leLens';

import { addDays, lastCompleteDay } from '../ProductPerformance/model';

import actionFields from './actionFields.json';
import type { FidelityCadence, FidelityFilterControl, FidelityFilters, FidelityViewId } from './presentationModel';
import config from './reportConfig.json';

export type LuxScope = LensFilterValues & { period: { window: { from: string; to: string }; comparison: null } };

const regionNames = new Intl.DisplayNames('en-AU', { type: 'region' });
export const countryLabel = (value: string) =>
  /^[A-Z]{2}$/.test(value)
    ? (countryNames.find((country) => country.code === value)?.name ?? regionNames.of(value) ?? value)
    : value;
export function countryMembers(value: string, domain: ReadonlyArray<string> | null): Array<string> {
  if (domain?.includes(value)) {
    return [value];
  }
  const matched = domain?.filter((raw) => countryLabel(raw) === value);
  return matched?.length ? matched : [value];
}

export const SAVED_STATUSES = 'Saved statuses (awaiting_dates + completed)';
export const scopeBindings: Record<string, string> = {
  itemStatus: 'item_status',
  memberType: 'member_type',
  memberFlag: 'luxplus_member_flag',
  vendor: 'vendor_name',
  product: 'product_name',
  category: 'category_name',
  channel: 'traffic_channel',
  brand: 'brand_name',
  promoFlag: 'promo_flag',
  promoCode: 'promo_code_name',
  saleType: 'sale_type',
  customerSegment: 'customer_segment',
  offerCountry: 'offer_country_name',
  orderItemCategories: 'order_item_categories',
  renewalStatus: 'renewal_status',
  subscriptionPeriod: 'subscription_period_number',
  isPromo: 'parent_sub_is_promo',
  cancellationStatus: 'luxplus_status',
  promoSplit: 'citi_promo',
};
const takeupKeys = [
  'itemStatus',
  'memberType',
  'memberFlag',
  'vendor',
  'product',
  'category',
  'channel',
  'brand',
  'promoFlag',
  'promoCode',
  'saleType',
  'customerSegment',
  'offerCountry',
  'orderItemCategories',
];
const labels: Record<string, string> = {
  itemStatus: 'Item status',
  memberType: 'Member type',
  memberFlag: 'LuxPlus member',
  vendor: 'Vendor',
  product: 'Product',
  category: 'Category',
  channel: 'Traffic channel',
  brand: 'Brand',
  promoFlag: 'Promo flag',
  promoCode: 'Promo code',
  saleType: 'Sale type',
  customerSegment: 'Customer segment',
  offerCountry: 'Offer country',
  orderItemCategories: 'Order item category',
  renewalStatus: 'Renewal status',
  subscriptionPeriod: 'Subscription period',
  isPromo: 'Parent subscription is promo',
  cancellationStatus: 'LuxPlus status',
  promoSplit: 'Citi Promo vs Organic',
  bracketMetric: 'Margin / TTV brackets',
  enrollmentMonth: 'Enrollment month',
  priceBand: 'Price band',
  priceBandBasis: 'Price band basis',
};
export function getFidelityDefaultFilters(view: string): Partial<FidelityFilters> {
  if (view.startsWith('takeup')) {
    return {
      ...Object.fromEntries(takeupKeys.map((key) => [key, 'All'])),
      priceBand: 'All',
      priceBandBasis: 'Order price',
    };
  }
  if (view === 'renewals') {
    return { renewalStatus: 'All', subscriptionPeriod: 'All', isPromo: 'All' };
  }
  if (view === 'cancellations') {
    return { cancellationStatus: 'ACTIVE', promoSplit: 'All', bracketMetric: 'Margin' };
  }
  if (view === 'cohorts') {
    return { enrollmentMonth: 'All' };
  }
  return {};
}
export function defaultScope(filters: FidelityFilters, view: FidelityViewId): FidelityFilters {
  return { ...getFidelityDefaultFilters(view), ...filters };
}
export const validDay = (v: string) =>
  v.slice(0, 4) !== '0000' &&
  /^\d{4}-\d{2}-\d{2}$/.test(v) &&
  Number.isFinite(Date.parse(v)) &&
  new Date(v).toISOString().slice(0, 10) === v;
export function requestLux(
  filters: FidelityFilters,
  view: FidelityViewId,
  cadence: FidelityCadence,
  params: URLSearchParams,
  now: Date,
  countryDomain: ReadonlyArray<string> | null = null,
): LuxScope {
  const selected = defaultScope(filters, view),
    to = lastCompleteDay(now),
    from = view === 'overview' ? '2024-01-01' : addDays(to, -27);
  const extra: LensFilterValues = Object.fromEntries(
    config.filters.map((f) => [f.id, params.getAll(f.id)]).filter(([, v]) => v.length),
  );
  for (const p of config.parameters) {
    extra[p.id] = params.get(p.id) ?? p.defaultValue;
  }
  for (const key of Object.keys(getFidelityDefaultFilters(view)).filter((key) => key in scopeBindings)) {
    if (!Object.prototype.hasOwnProperty.call(filters, key) && params.has(scopeBindings[key])) {
      continue;
    }
    const value = selected[key as keyof FidelityFilters];
    if (value && value !== 'All') {
      extra[scopeBindings[key]] = value === SAVED_STATUSES ? ['awaiting_dates', 'completed'] : [value];
    }
  }
  if (view.startsWith('takeup') && selected.priceBand && selected.priceBand !== 'All') {
    extra[selected.priceBandBasis === 'Max item price' ? 'max_item_price_band' : 'order_price_band'] = [
      selected.priceBand,
    ];
  }
  const country =
    view === 'overview' ? 'executive_country' : view === 'renewals' ? 'parent_membership_country' : 'country_code_2';
  if (filters.market !== 'All markets') {
    extra[country] = countryMembers(filters.market, countryDomain);
  }
  let start = filters.startDate || from,
    end = filters.endDate || to;
  if (view === 'cohorts') {
    const month = selected.enrollmentMonth;
    start = params.get('cohort_from') ?? (month && month !== 'All' ? month.slice(0, 7) + '-01' : '2024-06-01');
    end =
      params.get('cohort_to') ??
      (month && month !== 'All' && validDay(start)
        ? new Date(Date.UTC(Number(start.slice(0, 4)), Number(start.slice(5, 7)), 0)).toISOString().slice(0, 10)
        : to);
  }
  return {
    ...extra,
    grain: view === 'overview' ? 'day' : cadence,
    data_split: params.get('data_split') ?? 'All',
    period: { window: { from: start, to: end }, comparison: null },
  };
}
export function actionProblem(encoded: unknown, breakdown = false): string | null {
  if (encoded === undefined || encoded === '{}') {
    return null;
  }
  if (typeof encoded !== 'string') {
    return 'Invalid source action.';
  }
  try {
    const action: unknown = JSON.parse(encoded);
    if (!action || typeof action !== 'object' || Array.isArray(action)) {
      return 'Invalid source action.';
    }
    if (Object.keys(action).length === 0) {
      return null;
    }
    const data = action as Record<string, unknown>,
      panel = data.panel;
    if (typeof panel !== 'string' || !(panel in actionFields) || (breakdown && panel !== 'opportunities')) {
      return 'Unsupported action target.';
    }
    const dims = data.dimensions,
      values = data.values;
    if (
      !Array.isArray(dims) ||
      !Array.isArray(values) ||
      dims.length === 0 ||
      dims.length > 4 ||
      dims.length !== values.length
    ) {
      return 'Invalid source action dimensions.';
    }
    const allowed: Array<string> = actionFields[panel];
    if (dims.some((d) => typeof d !== 'string' || !allowed.includes(d))) {
      return 'Unsupported action field.';
    }
    if (
      values.some(
        (v) =>
          typeof v !== 'string' && (!Array.isArray(v) || !v.length || v.some((member) => typeof member !== 'string')),
      )
    ) {
      return 'Invalid source action values.';
    }
    return null;
  } catch {
    return 'Invalid source action JSON.';
  }
}
export function scopeProblem(filters: FidelityFilters, view: FidelityViewId, scope: LuxScope): string | null {
  const actionError = actionProblem(scope.lux_action) ?? actionProblem(scope.lux_breakdown_action, true);
  if (actionError) {
    return actionError;
  }
  const window = scope.period.window;
  if (!validDay(window.from) || !validDay(window.to) || window.from > window.to) {
    return 'Select valid From and To dates in order; values are withheld.';
  }
  if (view === 'cohorts' && filters.market !== 'All markets') {
    return 'Cohorts has no country dimension. Choose All markets.';
  }
  if (Object.values(scope).some((v) => Array.isArray(v) && v.some((x) => x === '(blank)'))) {
    return 'Use the literal source member rather than the display-only (blank) token.';
  }
  return null;
}
export function getLiveFilterOptions(
  view: FidelityViewId,
  filters: FidelityFilters,
  options: Record<string, Array<string>>,
  cohorts: Array<string>,
): Array<FidelityFilterControl> {
  const defaults = getFidelityDefaultFilters(view);
  const control = (key: keyof FidelityFilters, values: Array<string>, defaultValue = 'All'): FidelityFilterControl => ({
    key,
    label: labels[key] ?? key,
    options: [...new Set(values)],
    defaultValue,
  });
  if (view.startsWith('takeup')) {
    return [
      ...takeupKeys.map((key) =>
        control(key as keyof FidelityFilters, [
          'All',
          ...(key === 'itemStatus' ? [SAVED_STATUSES] : []),
          ...(options[scopeBindings[key]] ?? []),
        ]),
      ),
      control('priceBandBasis', ['Order price', 'Max item price'], 'Order price'),
      control('priceBand', [
        'All',
        ...(options[filters.priceBandBasis === 'Max item price' ? 'max_item_price_band' : 'order_price_band'] ?? []),
      ]),
    ];
  }
  if (view === 'renewals') {
    return ['subscriptionPeriod', 'renewalStatus', 'isPromo'].map((key) =>
      control(key as keyof FidelityFilters, ['All', ...(options[scopeBindings[key]] ?? [])]),
    );
  }
  if (view === 'cancellations') {
    return [
      control('cancellationStatus', ['All', ...(options.luxplus_status ?? [])], String(defaults.cancellationStatus)),
      control('promoSplit', ['All', 'Organic', 'Citi Promo']),
      control('bracketMetric', ['Margin', 'TTV', 'Savings'], 'Margin'),
    ];
  }
  if (view === 'cohorts') {
    return [control('enrollmentMonth', ['All', ...cohorts])];
  }
  return [];
}
